import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActionPanelComponent } from './components/action-panel/action-panel.component';
import { ChangeClientDialogComponent } from './components/change-client-dialog/change-client-dialog.component';
import { ChartSwitchComponent } from './components/chart-switch/chart-switch.component';
import { DirectoryCardComponent } from './components/directory-card/directory-card.component';
import { InsightCardComponent } from './components/insight-card/insight-card.component';
import { DisplayOptionsMenuComponent } from './components/display-options-menu/display-options-menu.component';
import { LargeCardComponent } from './components/large-card/large-card.component';
import { LGFooterComponent } from './components/lg-footer/lg-footer.component';
import { MainFiltersComponent } from './components/main-filters/main-filters.component';
import { MenuListComponent } from './components/menu-list/menu-list.component';
import { PageHeadlineComponent } from './components/page-headline/page-headline.component';
import { SearchBoxComponent } from './components/search-box/search-box.component';
import { SiteSearchComponent } from './components/site-search/site-search.component';
import { SideSheetComponent } from './components/side-sheet/side-sheet.component';
import { SmallCardComponent } from './components/small-card/small-card.component';
import { YearToggleComponent } from './components/year-toggle/year-toggle.component';
import { TabComponent } from './components/tab/tab.component';
import { ClientsService } from './services/clients.service';
import { FiltersService } from './services/filters.service';
import { MilestoneService } from './services/milestone.service';
import { UserService } from './services/user.service';
import { ThirdPartyModule } from '../third-party/third-party.module';
import { RouterModule } from '@angular/router';
import { NegativeToParensPipe } from './pipes/negative-to-parens.pipe';
import { TruncateTo25CharactersPipe } from './pipes/truncate-to-25-characters.pipe';
import { TruncateToCharsPipe } from './pipes/truncate-to-chars.pipe';
import { AuthGuardService } from './services/auth-guard.service';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CardListComponent } from './components/card-list/card-list.component';
import { SpinLoaderComponent } from './components/spin-loader/spin-loader.component';
import { ModuleUpgradeComponent } from './components/module-upgrade/module-upgrade.component';
import { VersionService } from './services/version.service';
import { ResetPasswordService } from './services/reset-password.service';
import { HelpModuleComponent } from './components/help-module-dialog/help-module.component';
import { ImpersonateDialogComponent } from './components/impersonate-dialog/impersonate-dialog.component';
import { HelpDialogComponent } from './components/help-module-dialog/help-dialog/help-dialog.component';
import { ToastComponent } from './components/toast/toast.component';
import { UserAccessService } from './services/user-access.service';
import { NumberToDecimalPrecision } from './pipes/number-precision.pipe';
import { DigitOnlyDirective } from './directive/digit-only.directive';
import { ClickOutsideDirective } from './directive/click-outside.directive';
import { SuccessfulSubmissionDialogComponent } from './components/successful-submission-dialog/successful-submission-dialog.component';
import { SearchDialogComponent } from './components/search-dialog/search-dialog.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FilePickerModule } from 'ngx-awesome-uploader';
import { LoanDetailsModalComponent } from './loan-details-modal/loan-details-modal.component';
import { TableDialogComponent } from './components/table-dialog/table-dialog.component';
import { NoDataScreenComponent } from './components/no-data-screen/no-data-screen.component';
import { DragndropTableComponent } from './components/dragndrop-table/dragndrop-table.component';
import { DialogNotificationsComponent } from './components/dialog-notifications/dialog-notifications.component';
import { DecimalPointPrecisionDirective } from '../settings/directives/decimal-point-precision.directive';
import { LgButtonComponent } from './components/lg-button/lg-button.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { SiteSearchDialogComponent } from './components/site-search-dialog/site-search-dialog.component';
import { AddJobTitleMappingDialogComponent } from './components/add-job-title-mapping-dialog/add-job-title-mapping-dialog.component';
import { SiteSearchService } from 'app/modules/shared/services/site-search.service';
import { EmployeeRecordViewComponent } from 'app/modules/settings/components/profile/my-record/employee-record-view/employee-record-view.component';
import { ColumnBarGraphComponent } from './components/column-bar-graph/column-bar-graph.component';
import { PropensityBandComponent } from './components/propensity-band/propensity-band.component';
import { EmailConfirmationDialogComponent } from './components/email-confirmation-dialog/email-confirmation-dialog.component';
import { ProfilePictureComponent } from './components/profile-picture/profile-picture.component';
import { ValidatorComponent } from './components/validator/validator.component';
import { SimpleTableCardComponent } from './components/simple-table-card/simple-table-card.component';
import { ConvertToUsersComponent } from './components/convert-to-users/convert-to-users.component';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { MainFiltersConfirmationDialogComponent } from './components/main-filters-confirmation-dialog/main-filters-confirmation-dialog.component';
import { UserActionsDialogComponent } from './components/user-actions-dialog/user-actions-dialog.component';
import { PositionDdlComponent } from './components/position-ddl/position-ddl.component';
import { LogoutIdleDialogComponent } from './components/logout-idle-dialog/logout-idle-dialog.component';
import { NoDirectoryCardComponent } from './components/no-directory-card/no-directory-card.component';
import { TransitionDirectoryCardsComponent } from './components/transition-directory-cards/transition-directory-cards.component';
import { InsightsDirectoryCardsComponent } from './components/insights-directory-cards/insights-directory-cards.component';
import { KendogridFilterDropdownComponent } from './components/kendogrid-filter-dropdown/kendogrid-filter-dropdown.component';
import { LoanCardDialogComponent } from './loan-details-modal/loan-card-dialog/loan-card-dialog.component';
import { LoanCardDetailsComponent } from './loan-details-modal/loan-card-details/loan-card-details.component';
import { LoanCardContactsComponent } from './loan-details-modal/loan-card-contacts/loan-card-contacts.component';
import { LoanCardRiskComponent } from './loan-details-modal/loan-card-risk/loan-card-risk.component';
import { LoanCardMilestonesComponent } from './loan-details-modal/loan-card-milestones/loan-card-milestones.component';
import { LoanCardActivityComponent } from './loan-details-modal/loan-card-activity/loan-card-activity.component';
import { LoanCardContactDialogComponent } from './loan-details-modal/loan-card-contact-dialog/loan-card-contact-dialog.component';
import { ScheduleReportBuilderComponent } from './components/schedule-report-builder/schedule-report-builder.component';
import { ScheduleReportToggleComponent } from './components/schedule-report-toggle/schedule-report-toggle.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { DonutChartComponent } from './components/donut-chart/donut-chart.component';
import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { CardControllerPanelComponent } from './components/card-controller-panel/card-controller-panel.component';
import { CustomDashboardsOutletDirective } from '../my-limegear/my-limegear-outlet.directive';
import { MappingTableComponent } from './components/mapping-table/mapping-table.component';
import { ScorecardsProductPurposeMixCardComponent } from './components/scorecards-product-purpose-mix-card/scorecards-product-purpose-mix-card.component';
import { ScorecardsFundedProductionCardComponent } from './components/scorecards-funded-production-card/scorecards-funded-production-card.component';
import { ScorecardsScoreSummaryCardComponent } from './components/scorecards-score-summary-card/scorecards-score-summary-card.component';
import { CommonKendoTableComponent } from './components/common-kendo-table/common-kendo-table.component';
import { LoanProfitabilityDialogComponent } from './components/loan-profitability-dialog/loan-profitability-dialog.component';
import { FinancialsAagDialogComponent } from './components/financials-aag-dialog/financials-aag-dialog.component';
import { DisplayOptionsDialogComponent } from './components/display-options-dialog/display-options-dialog.component';
import { CustomTableComponent } from './components/custom-table/custom-table.component';
import { CustomMatTableComponent } from './components/custom-mat-table/custom-mat-table.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { VersionMismatchDetectionService } from './services/version-mismatch-detection.service';
import { ToastService } from './services/toast.service';
import { SubscriptionsService } from './services/subscriptions.service';
import { ScheduledReportsService } from './services/scheduled-reports.service';
import { PerformanceScorecardService } from '../performance/services/performance-scorecard.service';
import { DisplayOptionsService } from './services/display-options.service';
import { EmployeeService } from './services/employee.service';
import { ErrorService } from './services/error-service';
import { ImpersonationService } from './services/impersonation.service';
import { KPIService } from './services/kpi.service';
import { LoggingService } from './services/logging-service - Copy';
import { LoginActivityService } from './services/login-activity.service';
import { NavigationHelpService } from './services/navigation-help.service';
import { NotificationService } from './services/notification.service';
import { PositionService } from './services/position.service';
import { ReportExportService } from './services/report-export.service';
import { NgxMaskModule } from 'ngx-mask';
import { DynamicDialogComponent } from './components/dynamic-dialog/dynamic-dialog.component';

const oktaConfig = {
    clientId: '0oaopwj8vXTSxV5gT5d6',
    issuer: 'https://dev-03884499.okta.com/oauth2/default',
    redirectUri: 'https://localhost:44388/login/callback/okta',
    scopes: ['openid', 'profile', 'email'],
    pkce: true,
};

const oktaAuth = new OktaAuth(oktaConfig);

@NgModule({
    declarations: [
        ActionPanelComponent,
        CardListComponent,
        ChangeClientDialogComponent,
        ChartSwitchComponent,
        DirectoryCardComponent,
        InsightCardComponent,
        DisplayOptionsMenuComponent,
        LargeCardComponent,
        LGFooterComponent,
        MainFiltersComponent,
        MainFiltersConfirmationDialogComponent,
        MenuListComponent,
        PageHeadlineComponent,
        SearchBoxComponent,
        SiteSearchComponent,
        SideSheetComponent,
        SmallCardComponent,
        YearToggleComponent,
        TabComponent,
        NegativeToParensPipe,
        TruncateTo25CharactersPipe,
        TruncateToCharsPipe,
        NumberToDecimalPrecision,
        CardListComponent,
        SpinLoaderComponent,
        ModuleUpgradeComponent,
        HelpModuleComponent,
        ImpersonateDialogComponent,
        ToastComponent,
        HelpDialogComponent,
        DigitOnlyDirective,
        SuccessfulSubmissionDialogComponent,
        SearchDialogComponent,
        FileUploadComponent,
        LoanDetailsModalComponent,
        TableDialogComponent,
        NoDataScreenComponent,
        ClickOutsideDirective,
        DragndropTableComponent,
        DialogNotificationsComponent,
        DecimalPointPrecisionDirective,
        LgButtonComponent,
        SiteSearchDialogComponent,
        AddJobTitleMappingDialogComponent,
        EmployeeRecordViewComponent,
        ColumnBarGraphComponent,
        PropensityBandComponent,
        EmailConfirmationDialogComponent,
        ProfilePictureComponent,
        ValidatorComponent,
        SimpleTableCardComponent,
        ConvertToUsersComponent,
        UserActionsDialogComponent,
        PositionDdlComponent,
        LogoutIdleDialogComponent,
        NoDirectoryCardComponent,
        TransitionDirectoryCardsComponent,
        InsightsDirectoryCardsComponent,
        KendogridFilterDropdownComponent,
        LoanCardDialogComponent,
        LoanCardDetailsComponent,
        LoanCardContactsComponent,
        LoanCardRiskComponent,
        LoanCardMilestonesComponent,
        LoanCardActivityComponent,
        LoanCardContactDialogComponent,
        ScheduleReportBuilderComponent,
        ScheduleReportToggleComponent,
        ConfirmationDialogComponent,
        DonutChartComponent,
        BarChartComponent,
        CardControllerPanelComponent,
        CustomDashboardsOutletDirective,
        MappingTableComponent,
        ScorecardsProductPurposeMixCardComponent,
        ScorecardsFundedProductionCardComponent,
        ScorecardsScoreSummaryCardComponent,
        CommonKendoTableComponent,
        LoanProfitabilityDialogComponent,
        FinancialsAagDialogComponent,
        DisplayOptionsDialogComponent,
        CustomTableComponent,
        CustomMatTableComponent,
        DynamicDialogComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        FilePickerModule,
        ScrollingModule,
        GridModule,
        ThirdPartyModule,
        OktaAuthModule,
        NgxMaskModule,
    ],
    exports: [
        ActionPanelComponent,
        CardListComponent,
        ChangeClientDialogComponent,
        ChartSwitchComponent,
        DirectoryCardComponent,
        InsightCardComponent,
        DisplayOptionsMenuComponent,
        LargeCardComponent,
        LGFooterComponent,
        MainFiltersComponent,
        MenuListComponent,
        PageHeadlineComponent,
        SearchBoxComponent,
        SiteSearchComponent,
        SideSheetComponent,
        SmallCardComponent,
        YearToggleComponent,
        TabComponent,
        NegativeToParensPipe,
        TruncateTo25CharactersPipe,
        TruncateToCharsPipe,
        NumberToDecimalPrecision,
        SpinLoaderComponent,
        ModuleUpgradeComponent,
        HelpModuleComponent,
        ToastComponent,
        DigitOnlyDirective,
        SearchDialogComponent,
        SuccessfulSubmissionDialogComponent,
        FileUploadComponent,
        LoanDetailsModalComponent,
        TableDialogComponent,
        NoDataScreenComponent,
        ClickOutsideDirective,
        DragndropTableComponent,
        DecimalPointPrecisionDirective,
        LgButtonComponent,
        AddJobTitleMappingDialogComponent,
        EmployeeRecordViewComponent,
        ColumnBarGraphComponent,
        PropensityBandComponent,
        EmailConfirmationDialogComponent,
        ProfilePictureComponent,
        ValidatorComponent,
        SimpleTableCardComponent,
        ConvertToUsersComponent,
        UserActionsDialogComponent,
        PositionDdlComponent,
        LogoutIdleDialogComponent,
        NoDirectoryCardComponent,
        InsightsDirectoryCardsComponent,
        TransitionDirectoryCardsComponent,
        KendogridFilterDropdownComponent,
        LoanCardDialogComponent,
        LoanCardContactDialogComponent,
        ScheduleReportBuilderComponent,
        ScheduleReportToggleComponent,
        ConfirmationDialogComponent,
        DonutChartComponent,
        BarChartComponent,
        CardControllerPanelComponent,
        CustomDashboardsOutletDirective,
        MappingTableComponent,
        ScorecardsProductPurposeMixCardComponent,
        ScorecardsFundedProductionCardComponent,
        ScorecardsScoreSummaryCardComponent,
        CommonKendoTableComponent,
        LoanProfitabilityDialogComponent,
        CustomTableComponent,
        CustomMatTableComponent,
        DynamicDialogComponent,
        ImpersonateDialogComponent,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true,
        },
        {
            provide: OKTA_CONFIG,
            useValue: {
                oktaAuth,
            },
        },
        AuthGuardService,
        ClientsService,
        NegativeToParensPipe,
        NumberToDecimalPrecision,
        TruncateTo25CharactersPipe,
        VersionService,
        DigitOnlyDirective,
        ClickOutsideDirective,
        UserService,
        VersionMismatchDetectionService,
        SubscriptionsService,
        SiteSearchService,
        ScheduledReportsService,
        ResetPasswordService,
        ReportExportService,
        PositionService,
        PerformanceScorecardService,
        NotificationService,
        NavigationHelpService,
        MilestoneService,
        LoginActivityService,
        LoggingService,
        KPIService,
        FiltersService,
        ErrorService,
        EmployeeService,
        DisplayOptionsService,
    ],
    schemas: [NO_ERRORS_SCHEMA],
})
export class SharedModule {}
